import { Injectable } from '@angular/core';
import { ReferenceFieldAPI } from '@core/typings/api/reference-fields.typing';
import { CustomDataTablesService } from '@features/custom-data-tables/services/custom-data-table.service';
import { UserService } from '@features/users/user.service';
import { TypeaheadSelectOption } from '@yourcause/common/core-forms';
import { ArrayHelpersService } from '@yourcause/common/utils';
import { FormFieldHelperService } from './form-field-helper.service';
import { FormDefinitionComponent } from '@features/configure-forms/form.typing';
import { ConfirmationModalComponent, ModalFactory } from '@yourcause/common/modals';
import { I18nService } from '@yourcause/common/i18n';

@Injectable({ providedIn: 'root' })
export class FormFieldCdtService {

  constructor (
    private customDataTableService: CustomDataTablesService,
    private userService: UserService,
    private formFieldHelperService: FormFieldHelperService,
    private arrayHelper: ArrayHelpersService,
    private i18n: I18nService,
    private modalFactory: ModalFactory
  ) { }

  get customDataTableOptionsMap () {
    return this.customDataTableService.customDataTableOptionsMap;
  }

  async getCdtOptionsFromRefField (
    field: ReferenceFieldAPI.ReferenceFieldDisplayModel
  ): Promise<TypeaheadSelectOption[]> {
    let options: TypeaheadSelectOption[] = [];
    if (!!field.customDataTableGuid && this.formFieldHelperService.isCustomDataTableType(field.type)) {
      if (!this.customDataTableOptionsMap[field.customDataTableGuid]) {
        await this.customDataTableService.setCustomDataTableOptionsFromGuid(
          field.customDataTableGuid,
          this.userService.getCurrentUserCulture()
        );
      }

      options = this.customDataTableOptionsMap[field.customDataTableGuid].map((option) => {
        return {
          label: option.value,
          value: option.key,
          hidden: !option.inUse
        };
      });
    }

    return options;
  }

  getFilterOptions (
    guid: string
  ) {
    return (this.customDataTableOptionsMap[guid] || []).map((opt) => {
      return {
        label: opt.value,
        value: opt.key,
        hidden: !opt.inUse
      };
    });
  }

  getParentRefFieldOptionsFromDependentCDTGuid (
    guid: string,
    refFieldId: number = null
  ): TypeaheadSelectOption<number>[] {
    // refFieldID is optionally passed in to check a very specific scenario
    // if you are editing a parent picklist and update the CDT to a child of the same CDT
    // you were previously using, this refFieldID will allow us to filter the results

    // default options to empty array for cdt without parentPicklist
    const cdt = this.customDataTableService.getCDTFromGuid(guid);
    let options: TypeaheadSelectOption<number>[] = [];

    if (cdt?.parentPicklistId) {
      // take incoming CDT and get parent
      const parentCDT = this.customDataTableService.getCDTFromId(cdt.parentPicklistId);
      // filter ref fields down to those that have matching guid to parent
      const filteredRefFields = this.formFieldHelperService.allReferenceFields.filter((refField) => {
        return refField.customDataTableGuid === parentCDT.guid && refField.referenceFieldId !== refFieldId;
      });
      options = filteredRefFields.map((refField) => {
        return {
          label: refField.name,
          value: refField.referenceFieldId
        };
      });
    }
    const sortedOptions = this.arrayHelper.sort(options, 'label');

    return sortedOptions;
  }

  /**
   * Get Parent Picklists to add to the form
   *
   * @param field: Field to check for parent picklists
   * @param parentPicklists: Array of parent picklists to add to
   * @returns Array of parent picklists to add to the form 
   */
  getParentPicklistsToAdd (
    field: ReferenceFieldAPI.ReferenceFieldDisplayModel,
    parentPicklists: ReferenceFieldAPI.ReferenceFieldDisplayModel[] = []
  ): ReferenceFieldAPI.ReferenceFieldDisplayModel[] {
    if (!!field) {
      const requiresParent = !!field.parentReferenceFieldId && !field.aggregateType;
      if (requiresParent) {
        const {
          parentIsOnForm,
          parentPicklist
        } = this.checkForParentPicklistOnForm(
          field.parentReferenceFieldId
        );
        if (!parentIsOnForm && !!parentPicklist) {
          parentPicklists.push(parentPicklist);

          return this.getParentPicklistsToAdd(parentPicklist, parentPicklists);
        }
      }
    }

    return parentPicklists
  }

  checkForParentPicklistOnForm (parentPicklistId: number): {
    parentIsOnForm: boolean;
    parentPicklist: ReferenceFieldAPI.ReferenceFieldDisplayModel;
  } {
    const parentIsOnForm = this.formFieldHelperService.currentFormRefFields.some((refField) => {
      return refField.referenceFieldId === parentPicklistId;
    });
    const parentPicklist = this.formFieldHelperService.allReferenceFields.find((refField) => {
      return refField.referenceFieldId === parentPicklistId;
    });

    return {
      parentIsOnForm,
      parentPicklist
    };
  }


  /**
   * Handles determining if by adding this field to a form we also need to add any parent picklists
   *
   * @param component: Component to handle dependent picklists for
   * @returns the proceed flag and parent picklists to add
   */
  async handleAddingDependentPicklistToForm (
    component: FormDefinitionComponent
  ): Promise<{
    proceed: boolean;
    parentPicklistsToAdd: ReferenceFieldAPI.ReferenceFieldDisplayModel[]
  }> {
    const field = this.formFieldHelperService.getReferenceFieldFromCompType(component.type);
    const parentPicklistsToAdd = this.getParentPicklistsToAdd(field);
    
    if (parentPicklistsToAdd.length > 0) {
      const isOne = parentPicklistsToAdd.length === 1;
      const proceed = await this.modalFactory.open(
        ConfirmationModalComponent,
        {
          modalHeader: this.i18n.translate(
            isOne ? 'common:hdrAddParentPicklist' : 'common:hdrAddParentPicklists',
            {},
            isOne ? 'Add Parent Picklist' : 'Add Parent Picklists'
          ),
          confirmText: this.i18n.translate(
            isOne ?
              'common:textAddParentPicklistDesc' :
              'common:textAddParentPicklistsDesc',
            {},
            isOne ?
              'The field you are trying to add is a child of a picklist that is not on the form. By adding this field, you will also add the parent picklist to the form. Do you want to proceed?' :
              'The fields you are trying to add are children of picklists that are not on the form. By adding these fields, you will also add the parent picklists to the form. Do you want to proceed?'
          ),
          confirmButtonText: this.i18n.translate(
            'common:linkAdd',
            {},
            'Add'
          ),
          cancelButtonText: this.i18n.translate(
            'common:btnCancel',
            {},
            'Cancel'
          )
        }
      );
      if (!proceed) {
        return {
          proceed: false,
          parentPicklistsToAdd: []
        };
      }
    }

    return {
      proceed: true,
      parentPicklistsToAdd
    };
  }
}
